import { useState } from "react";
import { Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useSearchableList } from "@/hooks/useSearchableList";
import { PageHeader, Field, EmptyState, inputCls, withRef } from "@/components/shared";

const emptyTask = { title: "", due_date: "", priority: "medium", linked_type: "", linked_id: "", linked_name: "", status: "pending", notes: "" };
const LINK_ENDPOINTS = { buyer: "/buyers", seller: "/sellers", property: "/properties", viewing: "/viewings", lead: "/leads" };

export default function Tasks() {
  const { items, search, setSearch, reload } = useSearchableList("/tasks");
  const [form, setForm] = useState(null);
  const [linkOptions, setLinkOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const { t, lang } = useLang();

  const today = new Date().toISOString().slice(0, 10);

  const loadLinkOptions = async (type) => {
    if (!type || !LINK_ENDPOINTS[type]) return setLinkOptions([]);
    const { data } = await api.get(LINK_ENDPOINTS[type]);
    setLinkOptions(data.map((d) => ({
      id: d.id,
      ref_code: d.ref_code || "",
      name: d.name || `${d.buyer_name} – ${d.property_name}`,
    })));
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error(t("required"));
    setSaving(true);
    try {
      if (form.id) await api.put(`/tasks/${form.id}`, form);
      else await api.post("/tasks", form);
      toast.success(t("saved"));
      setForm(null);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleDone = async (task) => {
    const status = task.status === "done" ? "pending" : "done";
    await api.put(`/tasks/${task.id}`, { status });
    reload();
  };

  const remove = async () => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/tasks/${form.id}`);
    toast.success(t("deleted"));
    setForm(null);
    reload();
  };

  const priorityColor = { high: "bg-orange-50 text-[#9A3412]", medium: "bg-stone-100 text-stone-600", low: "bg-stone-50 text-stone-400" };
  const sorted = [...items].sort((a, b) => (a.status === "done") - (b.status === "done") || (a.due_date || "9999").localeCompare(b.due_date || "9999"));

  return (
    <div data-testid="tasks-page">
      <PageHeader
        title={t("tasks")}
        action={
          <button data-testid="add-task-button" onClick={() => { setForm({ ...emptyTask }); setLinkOptions([]); }}
            className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors">
            <Plus size={16} strokeWidth={1.5} /> {t("addTask")}
          </button>
        }
      />
      <div className="relative mb-6 max-w-md">
        <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <input data-testid="tasks-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder={t("search")} className={`${inputCls} pl-9`} />
      </div>

      {sorted.length === 0 ? <EmptyState message={t("noResults")} /> : (
        <div className="space-y-2.5">
          {sorted.map((task) => {
            const overdue = task.status === "pending" && task.due_date && task.due_date < today;
            return (
              <div key={task.id} data-testid={`task-item-${task.id}`}
                className={`flex items-center gap-4 bg-white border rounded-xl px-5 py-4 transition-all duration-200 ${overdue ? "border-orange-200" : "border-stone-200 hover:border-stone-300"}`}>
                <Checkbox
                  data-testid={`task-toggle-${task.id}`}
                  checked={task.status === "done"}
                  onCheckedChange={() => toggleDone(task)}
                />
                <button className="flex-1 text-left" data-testid={`task-edit-${task.id}`}
                  onClick={() => { setForm({ ...emptyTask, ...task }); loadLinkOptions(task.linked_type); }}>
                  <p className={`text-sm font-medium ${task.status === "done" ? "text-stone-400 line-through" : "text-stone-800"}`}>{task.title}</p>
                  <p className="text-xs text-stone-400 mt-0.5">
                    {task.due_date ? fmtDate(task.due_date, lang) : "—"}
                    {overdue && <span className="text-[#9A3412] font-medium"> · {t("overdue")}</span>}
                    {task.linked_name && <span> · {task.linked_name}</span>}
                  </p>
                  {task.notes && (
                    <p data-testid={`task-notes-${task.id}`} className="text-xs text-stone-500 mt-1 line-clamp-2 whitespace-pre-wrap">{task.notes}</p>
                  )}
                </button>
                <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${priorityColor[task.priority] || priorityColor.medium}`}>
                  {t(task.priority)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-xl bg-white">
          {form && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl font-medium text-stone-900">
                  {form.id ? t("editTask") : t("addTask")}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <Field label={t("taskTitle")} className="sm:col-span-2">
                  <input data-testid="task-title-input" className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </Field>
                <Field label={t("dueDate")}>
                  <input data-testid="task-due-input" type="date" className={inputCls} value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                </Field>
                <Field label={t("priority")}>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="task-priority-select" className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">{t("high")}</SelectItem>
                      <SelectItem value="medium">{t("medium")}</SelectItem>
                      <SelectItem value="low">{t("low")}</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t("linkedType")}>
                  <Select value={form.linked_type || "none"}
                    onValueChange={(v) => {
                      const type = v === "none" ? "" : v;
                      setForm({ ...form, linked_type: type, linked_id: "", linked_name: "" });
                      loadLinkOptions(type);
                    }}>
                    <SelectTrigger data-testid="task-linktype-select" className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t("none")}</SelectItem>
                      <SelectItem value="buyer">{t("buyer")}</SelectItem>
                      <SelectItem value="seller">{t("seller")}</SelectItem>
                      <SelectItem value="property">{t("property")}</SelectItem>
                      <SelectItem value="viewing">{t("viewing")}</SelectItem>
                      <SelectItem value="lead">{t("lead")}</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t("linkedRecord")}>
                  <Select value={form.linked_id || "none"} disabled={!form.linked_type}
                    onValueChange={(v) => {
                      const opt = linkOptions.find((o) => o.id === v);
                      setForm({ ...form, linked_id: v === "none" ? "" : v, linked_name: opt ? withRef(opt.ref_code, opt.name) : "" });
                    }}>
                    <SelectTrigger data-testid="task-linkrecord-select" className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t("none")}</SelectItem>
                      {linkOptions.map((o) => <SelectItem key={o.id} value={o.id}>{withRef(o.ref_code, o.name)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t("notes")} className="sm:col-span-2">
                  <textarea data-testid="task-notes-input" rows={4} className={inputCls} value={form.notes || ""}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </Field>
              </div>
              <div className="flex items-center justify-between mt-6">
                {form.id ? (
                  <button data-testid="delete-task-button" onClick={remove} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#7F1D1D] hover:underline">
                    <Trash2 size={14} strokeWidth={1.5} /> {t("delete")}
                  </button>
                ) : <span />}
                <div className="flex gap-3">
                  <button data-testid="cancel-task-button" onClick={() => setForm(null)} className="bg-white text-stone-700 border border-stone-200 hover:bg-stone-50 rounded-lg px-5 py-2 text-sm font-medium transition-colors">{t("cancel")}</button>
                  <button data-testid="save-task-button" onClick={save} disabled={saving} className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-5 py-2 text-sm font-medium transition-colors disabled:opacity-60">{t("save")}</button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
